/**
 * LeadAssign — /#/leads/:id/assign
 *
 * Step 3 of the Lead-to-AE demo ribbon.
 * Shows a prospect summary card + AE dropdown populated from /api/ae-users
 * (tenant-scoped, no hardcoded fixtures).
 *
 * Primary CTA: "Assign and convert to prospect"
 * - Calls POST /api/prospects/:id/promote
 * - Persists: creates accounts row, status=Prospect, owner_user_id=aeUuid
 * - Navigates to /#/leads/:prospectId/success?accountId=:accountId&aeName=:name
 *
 * Error states surfaced via toast, not silent navigations.
 * Empty-AE state shown explicitly with clear message.
 */

import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/session";
import { RibbonProgress } from "./RibbonProgress";

type AeUser = { id: string; name: string; email: string };

// Map source DB values back to human labels for the summary card
const SOURCE_LABELS: Record<string, string> = {
  marketing_mql: "Inbound",
  cold_outbound: "Cold outbound",
  event:         "Event / Webinar",
  referral:      "Referral",
  manager_assigned: "Manager assigned",
};

export default function LeadAssign() {
  const params = useParams<{ id: string }>();
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const prospectId = params.id;

  // Read bantNeed from query string (passed from LeadQualify).
  // Wouter hash routing: params appear in the hash fragment or outer query string.
  const bantNeedFromQualify = (() => {
    const hash = window.location.hash;
    const qIdx = hash.indexOf("?");
    const params = qIdx !== -1
      ? new URLSearchParams(hash.slice(qIdx + 1))
      : new URLSearchParams(window.location.search);
    return decodeURIComponent(params.get("bant") ?? "");
  })();

  const [prospect, setProspect]   = useState<any>(null);
  const [aeList, setAeList]       = useState<AeUser[]>([]);
  const [selectedAe, setSelectedAe] = useState<string>("");
  const [loading, setLoading]     = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [aeError, setAeError]     = useState("");

  useEffect(() => {
    if (!prospectId) return;
    let cancelled = false;

    async function load() {
      try {
        const [prospectData, aes] = await Promise.all([
          api<any>(`/api/prospects/${prospectId}`),
          api<AeUser[]>("/api/ae-users"),
        ]);
        if (cancelled) return;
        setProspect(prospectData);
        setAeList(aes);
        if (aes.length > 0) setSelectedAe(aes[0].id);
        if (aes.length === 0) {
          setAeError("No AEs are set up in this tenant. Contact your admin to add AE accounts.");
        }
      } catch (e: any) {
        if (!cancelled) {
          toast({
            title: "Could not load data",
            description: e?.message ?? "Check your connection.",
            variant: "destructive",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [prospectId]);

  async function handleAssign() {
    if (!selectedAe || assigning) return;
    setAssigning(true);
    try {
      const result = await api<{
        ok: boolean;
        accountId: number;
        aeUuid: string;
        aeName: string;
        accountName: string;
      }>(`/api/prospects/${prospectId}/promote`, {
        method: "POST",
        body: JSON.stringify({
          aeUserUuid: selectedAe,
          bantNeed: bantNeedFromQualify || undefined,
        }),
      });

      const aeName = encodeURIComponent(result.aeName ?? "AE");
      navigate(
        `/leads/${prospectId}/success?accountId=${result.accountId}&aeName=${aeName}`
      );
    } catch (e: any) {
      toast({
        title: "Assignment failed",
        description: e?.message ?? "The server returned an error. Please try again.",
        variant: "destructive",
      });
      setAssigning(false);
    }
  }

  // ─── Skeleton loader ───────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <header className="sticky top-0 z-40 bg-white border-b border-neutral-200">
          <div className="max-w-2xl mx-auto px-5 py-3.5 flex items-center justify-between">
            <div className="h-4 w-32 bg-neutral-200 rounded animate-pulse" />
            <div className="text-[10px] text-neutral-500">Step 4 of 5</div>
          </div>
          <RibbonProgress step={4} />
        </header>
        <main className="max-w-2xl mx-auto px-5 py-8 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-neutral-200 rounded-xl animate-pulse" />
          ))}
        </main>
      </div>
    );
  }

  const selectedAeObj = aeList.find((a) => a.id === selectedAe);

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-neutral-200">
        <div className="max-w-2xl mx-auto px-5 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/leads/${prospectId}/qualify`)}
              className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
              aria-label="Back to qualification"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                <path d="M11 13.5L6.5 9 11 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 leading-none mb-0.5">Mi6 Attilius</div>
              <div className="text-sm font-bold text-[#0B0B0F] leading-none">Assign to AE</div>
            </div>
          </div>
          <div className="text-[10px] text-neutral-500">Step 4 of 5</div>
        </div>
        <RibbonProgress step={4} />
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8">
        <div className="mb-7">
          <h1 className="text-[22px] font-bold text-[#0B0B0F] tracking-tight">Convert and assign</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Choose the AE who will own this account. This creates the account in their pipeline immediately.
          </p>
        </div>

        {/* P0-5 — surface BDR step status: qualified vs. override */}
        {prospect?.bdr_override ? (
          <div className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-orange-50 border border-orange-200" data-testid="override-badge">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0 mt-0.5" aria-hidden>
              <circle cx="9" cy="9" r="8" stroke="#CA8A04" strokeWidth="1.5" />
              <path d="M9 5v5" stroke="#CA8A04" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="9" cy="13" r="0.75" fill="#CA8A04" />
            </svg>
            <div>
              <div className="text-sm font-semibold text-yellow-900">Override active — BDR step bypassed</div>
              <p className="text-[12px] text-yellow-800 mt-0.5">
                This lead was created with “Skip BDR” enabled. The override is logged on the prospect record.
              </p>
            </div>
          </div>
        ) : prospect?.bdr_disposition === "qualified" ? (
          <div className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="bdr-qualified-badge">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0 mt-0.5" aria-hidden>
              <circle cx="9" cy="9" r="8" stroke="#047857" strokeWidth="1.5" />
              <path d="M5.5 9.2l2.4 2.4 4.6-5.1" stroke="#047857" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>
              <div className="text-sm font-semibold text-emerald-900">BDR qualified — ready for AE</div>
              <p className="text-[12px] text-emerald-800 mt-0.5">
                M-PACT signals confirmed. Disposition: <strong>Qualified</strong>.
              </p>
            </div>
          </div>
        ) : null}

        {/* Prospect summary card */}
        {prospect && (
          <div
            className="bg-white rounded-2xl border p-5 mb-5 shadow-sm"
            style={{ borderColor: "#0B0B0F20" }}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">
              Lead summary
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <SummaryRow label="Company" value={prospect.company} />
              <SummaryRow label="Contact"  value={prospect.full_name} />
              {prospect.title && <SummaryRow label="Title" value={prospect.title} />}
              {prospect.source && (
                <SummaryRow
                  label="Source"
                  value={SOURCE_LABELS[prospect.source] ?? prospect.source}
                />
              )}
            </div>

            {bantNeedFromQualify && (
              <div className="mt-4 pt-4 border-t border-neutral-100">
                <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-semibold mb-1">
                  Qualification notes
                </div>
                <p className="text-sm text-neutral-700 leading-relaxed">
                  {bantNeedFromQualify}
                </p>
              </div>
            )}
          </div>
        )}

        {/* AE assignment section */}
        <div className="bg-white rounded-2xl border border-neutral-200 p-5 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-3">
            Assign to AE
          </div>

          {aeError ? (
            /* Empty state */
            <div className="flex items-start gap-3 p-4 rounded-xl bg-orange-50 border border-orange-200">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="shrink-0 mt-0.5" aria-hidden>
                <circle cx="9" cy="9" r="8" stroke="#CA8A04" strokeWidth="1.5" />
                <path d="M9 5v5" stroke="#CA8A04" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="9" cy="13" r="0.75" fill="#CA8A04" />
              </svg>
              <p className="text-sm text-yellow-800">{aeError}</p>
            </div>
          ) : (
            <>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-1.5">
                Account executive
              </label>
              <select
                className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-800 focus:border-[#0B0B0F] focus:ring-2 focus:ring-neutral-200/60 outline-none transition-all cursor-pointer"
                value={selectedAe}
                onChange={(e) => setSelectedAe(e.target.value)}
              >
                {aeList.map((ae) => (
                  <option key={ae.id} value={ae.id}>
                    {ae.name} ({ae.email})
                  </option>
                ))}
              </select>

              {selectedAeObj && (
                <div className="mt-3 flex items-center gap-2.5 p-3 rounded-lg bg-neutral-50 border border-neutral-100">
                  {/* Avatar initial */}
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                    style={{ background: "#0B0B0F" }}
                  >
                    {selectedAeObj.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0B0B0F] truncate">
                      {selectedAeObj.name}
                    </div>
                    <div className="text-[11px] text-neutral-500 truncate">
                      {selectedAeObj.email}
                    </div>
                  </div>
                  <div className="ml-auto shrink-0">
                    <span className="text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full bg-neutral-200 text-neutral-600">
                      AE
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* What happens next */}
        <div className="mt-5 px-4 py-3.5 rounded-xl bg-neutral-50 border border-neutral-200">
          <p className="text-[11px] text-neutral-500 leading-relaxed">
            Clicking "Assign and convert" will create this as a{" "}
            <span className="font-semibold text-neutral-700">Prospect</span> in Attilius and
            assign it to {selectedAeObj?.name ?? "the chosen AE"}.
            They will see it on their dashboard immediately.
          </p>
        </div>

        {/* CTAs */}
        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            disabled={!selectedAe || !!aeError || assigning}
            onClick={handleAssign}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#DC2626] text-white text-sm font-bold tracking-wide hover:bg-[#B91C1C] transition-all duration-150 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {assigning ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                </svg>
                Assigning...
              </>
            ) : (
              <>
                Assign and convert to prospect
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/leads/${prospectId}/qualify`)}
            className="px-4 py-3 rounded-xl border border-neutral-200 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 transition-colors"
          >
            Back
          </button>
        </div>
      </main>
    </div>
  );
}

// ─── Summary row ──────────────────────────────────────────────────────────────
function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-semibold mb-0.5">
        {label}
      </div>
      <div className="text-sm font-medium text-[#0B0B0F]">{value}</div>
    </div>
  );
}
